import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer, ShieldCheck, Trash2 } from 'lucide-react';
import { journalSuppressionsApi } from '../../utils/api';

const fmtDateHeure = (d) => {
  if (!d) return '';
  const dt = new Date((d.includes('T') ? d : d.replace(' ', 'T')) + (d.includes('Z') ? '' : 'Z'));
  return isNaN(dt) ? d : dt.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TRAITEMENTS = [
  {
    n: 1, titre: 'Gestion des préinscriptions et inscriptions',
    finalite: "Recueillir et instruire les demandes d'accueil, gérer la liste d'attente, proposer une place.",
    base: 'Mesures précontractuelles (art. 6.1.b) et consentement pour la demande en ligne (art. 6.1.a).',
    personnes: "Enfants à accueillir, représentants légaux.",
    donnees: "Identité de l'enfant et des parents, coordonnées, date de naissance/terme, jours et horaires souhaités, commune, revenu fiscal de référence, commentaires.",
    destinataires: "Personnel administratif autorisé de la structure.",
    conservation: "3 ans à compter du dernier contact si la demande n'aboutit pas ; sinon, bascule vers le dossier d'accueil.",
  },
  {
    n: 2, titre: "Contrats d'accueil et facturation",
    finalite: "Établir et exécuter le contrat d'accueil, facturer les familles, gérer les aides (CMG/CAF).",
    base: "Exécution du contrat (art. 6.1.b) et obligations légales comptables et fiscales (art. 6.1.c).",
    personnes: "Enfants accueillis, représentants légaux.",
    donnees: "Identité, coordonnées, coordonnées bancaires (mandat SEPA), heures d'accueil, montants facturés et réglés.",
    destinataires: "Personnel autorisé, expert-comptable mandaté, CAF/URSSAF le cas échéant.",
    conservation: "Pièces comptables : 10 ans. Contrat : durée de l'accueil + 5 ans.",
  },
  {
    n: 3, titre: "Suivi de l'enfant et données de santé (PAI, vaccinations)", sensible: true,
    finalite: "Assurer l'accueil, la sécurité et la santé de l'enfant (protocoles, allergies, PAI, vaccinations).",
    base: "Sauvegarde des intérêts vitaux et intérêt public dans le domaine de la santé (art. 9.2.c/h), consentement des responsables légaux lorsqu'il est requis, obligations réglementaires PMI.",
    personnes: "Enfants accueillis.",
    donnees: "Données de santé : allergies, PAI, vaccinations, antécédents utiles, coordonnées du médecin.",
    destinataires: "Personnel encadrant habilité, médecin de la structure / PMI le cas échéant.",
    conservation: "Durée de l'accueil, puis archivage selon les obligations réglementaires.",
  },
  {
    n: 4, titre: 'Transmissions quotidiennes',
    finalite: "Informer les représentants légaux du déroulé de la journée (sommeil, repas, change, activités).",
    base: "Exécution du contrat d'accueil (art. 6.1.b).",
    personnes: "Enfants accueillis.",
    donnees: "Rythme quotidien : sommeil, repas, change, activités, observations.",
    destinataires: "Représentants légaux, personnel encadrant.",
    conservation: "Durée de l'accueil (données courantes archivées / effacées en fin de période).",
  },
  {
    n: 5, titre: 'Gestion des ressources humaines', sensible: true,
    finalite: "Gérer les salariés : contrats, paie, congés, planning, formation, suivi du temps de travail.",
    base: "Exécution du contrat de travail (art. 6.1.b) et obligations légales sociales (art. 6.1.c). Données de santé (arrêts) : obligation légale (art. 9.2.b).",
    personnes: "Salariés de la structure.",
    donnees: "Identité, coordonnées, RIB, éléments de contrat, heures, heures supplémentaires, absences, congés, formation, arrêts de travail.",
    destinataires: "Direction, service/expert paie, organismes sociaux (URSSAF, mutuelle, médecine du travail).",
    conservation: "Bulletins de paie : 5 ans (conservation conseillée 50 ans). Dossier salarié : 5 ans après le départ.",
  },
  {
    n: 6, titre: "Gestion des accès à l'outil (comptes utilisateurs)",
    finalite: "Sécuriser l'accès à l'application de gestion et tracer les connexions.",
    base: "Intérêt légitime : sécurité du système d'information (art. 6.1.f).",
    personnes: "Personnel disposant d'un accès.",
    donnees: "Identifiant, adresse e-mail, mot de passe (haché), rôle/droits, dates de connexion.",
    destinataires: "Administrateur de la structure.",
    conservation: "Durée de la relation, puis suppression du compte.",
  },
];

const Field = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-x-4 gap-y-0.5 py-1.5 border-b border-cream-200 last:border-0">
    <div className="text-[12px] font-semibold uppercase tracking-wide text-gold-700">{label}</div>
    <div className="text-[13.5px] text-navy-700 leading-relaxed">{children}</div>
  </div>
);

export default function RegistreTraitements() {
  const [suppressions, setSuppressions] = useState(null); // null = pas chargé / non autorisé
  useEffect(() => {
    journalSuppressionsApi.list({ limit: 500 })
      .then(({ data }) => setSuppressions(Array.isArray(data) ? data : []))
      .catch(() => setSuppressions(null)); // non connecté / non autorisé → section masquée
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-100 to-cream-200 py-10 px-4 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-navy-500 hover:text-navy-800">
            <ArrowLeft size={15} /> Retour
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-navy-700 hover:bg-navy-800 rounded-lg px-3.5 py-2">
            <Printer size={15} /> Imprimer / PDF
          </button>
        </div>

        <div className="bg-white rounded-[22px] shadow-xl p-8 md:p-10 print:shadow-none print:rounded-none print:p-0">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-gold-600" />
            <h1 className="font-serif text-2xl text-navy-900">Registre des activités de traitement</h1>
          </div>
          <p className="text-navy-300 text-sm mb-6">Conforme à l'article 30 du RGPD — micro-crèche</p>

          {/* Éléments généraux */}
          <div className="rounded-xl border border-cream-300/70 p-4 mb-8 text-[13.5px] text-navy-700 space-y-1">
            <div><span className="text-navy-400">Responsable de traitement :</span> <strong>SAS Les Cachettes</strong> (et SAS La Cachette des Ptis Lu pour Le Pallet), représentées par Marion Lumière.</div>
            <div><span className="text-navy-400">Référent / contact données :</span> Marion Lumière — <strong>contact@lescachettes.fr</strong>.</div>
            <div><span className="text-navy-400">Sous-traitant (hébergement) :</span> Infomaniak Network SA (Genève, Suisse — pays reconnu adéquat par la Commission européenne).</div>
            <div><span className="text-navy-400">Sous-traitant (assistant IA, facultatif) :</span> Anthropic PBC (États-Unis). Données <strong>pseudonymisées avant transmission</strong> (aucun nom en clair) ; transfert encadré par les clauses contractuelles types (SCC).</div>
            <div><span className="text-navy-400">Éditeur du logiciel :</span> Hello Georges (contrat de sous-traitance RGPD art. 28).</div>
            <div><span className="text-navy-400">Transferts hors UE :</span> hébergement en Suisse (décision d'adéquation) ; recours facultatif à un assistant IA aux États-Unis sur données pseudonymisées, encadré par les clauses contractuelles types.</div>
            <div><span className="text-navy-400">Mesures de sécurité :</span> connexion chiffrée (HTTPS), mots de passe hachés, accès restreint par rôles, journalisation, sauvegardes régulières.</div>
          </div>

          {/* Traitements */}
          <div className="space-y-5">
            {TRAITEMENTS.map((t) => (
              <div key={t.n} className="rounded-xl border border-cream-300/70 overflow-hidden print:break-inside-avoid">
                <div className="px-4 py-2.5 bg-cream-50 border-b border-cream-200 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-navy-700 text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">{t.n}</span>
                  <h2 className="font-semibold text-navy-800 text-[15px]">{t.titre}</h2>
                  {t.sensible && <span className="ml-auto text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Données sensibles</span>}
                </div>
                <div className="px-4 py-2">
                  <Field label="Finalité">{t.finalite}</Field>
                  <Field label="Base légale">{t.base}</Field>
                  <Field label="Personnes concernées">{t.personnes}</Field>
                  <Field label="Catégories de données">{t.donnees}</Field>
                  <Field label="Destinataires">{t.destinataires}</Field>
                  <Field label="Durée de conservation">{t.conservation}</Field>
                </div>
              </div>
            ))}
          </div>

          {/* Registre des suppressions définitives (droit à l'effacement, art. 17) — visible aux gestionnaires connectés */}
          {suppressions && (
            <div className="pt-6 mt-6 border-t border-cream-200 print:break-inside-avoid">
              <h2 className="flex items-center gap-2 font-serif text-[18px] text-navy-900 mb-1">
                <Trash2 size={17} className="text-rose-500" /> Registre des suppressions (droit à l'effacement)
              </h2>
              <p className="text-[12.5px] text-navy-500 mb-3">
                Trace de chaque suppression définitive de donnée (art. 17 RGPD) : date, nature, motif et auteur. {suppressions.length} enregistrement(s).
              </p>
              {suppressions.length === 0 ? (
                <p className="text-[13px] text-navy-300">Aucune suppression enregistrée à ce jour.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="text-left text-navy-400 border-b border-cream-200">
                        <th className="py-1.5 pr-3 font-semibold">Date</th>
                        <th className="py-1.5 pr-3 font-semibold">Type</th>
                        <th className="py-1.5 pr-3 font-semibold">Donnée supprimée</th>
                        <th className="py-1.5 pr-3 font-semibold">Motif</th>
                        <th className="py-1.5 font-semibold">Auteur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppressions.map(s => (
                        <tr key={s.id} className="border-b border-cream-100 align-top">
                          <td className="py-1.5 pr-3 whitespace-nowrap text-navy-500">{fmtDateHeure(s.supprime_le)}</td>
                          <td className="py-1.5 pr-3 text-navy-600">{s.entite_type}</td>
                          <td className="py-1.5 pr-3 text-navy-700">{s.libelle || '—'}</td>
                          <td className="py-1.5 pr-3 text-navy-600">{s.motif || '—'}</td>
                          <td className="py-1.5 text-navy-600 whitespace-nowrap">{s.auteur || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <p className="text-[12px] text-navy-300 pt-6 mt-6 border-t border-cream-200">
            Registre tenu à jour par le responsable de traitement et mis à disposition de l'autorité de contrôle (CNIL) sur demande.
            Dernière mise à jour : 22 juillet 2026.
          </p>
        </div>
      </div>
    </div>
  );
}
